<template>
  <div class="customer-list-container">
    <!-- Modern Search and Filter Section -->
    <div class="search-filter-section">
      <v-card class="search-filter-card" elevation="2">
        <v-card-text class="search-filter-content">
          <!-- Action Bar -->
          <div class="action-bar">
            <div class="search-title">
              <h3 class="filter-title">
                Customer Search & Filters
                <v-btn
                  icon
                  @click="toggleSearchExpansion"
                  class="expansion-btn"
                  :class="{ 'expansion-btn--expanded': isSearchExpanded }"
                >
                  <v-icon>{{
                    isSearchExpanded ? "expand_less" : "expand_more"
                  }}</v-icon>
                </v-btn>
              </h3>
              <p class="filter-subtitle" v-show="isSearchExpanded">
                Find and manage customer accounts
              </p>
              <p class="filter-subtitle-collapsed" v-show="!isSearchExpanded">
                {{ getActiveFiltersText() }}
              </p>
            </div>

            <v-btn
              color="#fddf00"
              @click="toAdd()"
              :disabled="isEditCustomer"
              class="add-customer-btn"
            >
              <v-icon>add</v-icon>
              Add Customer
            </v-btn>
          </div>

          <!-- Search Grid with Expansion -->
          <v-expand-transition>
            <div v-show="isSearchExpanded" class="search-expansion-content">
              <div class="search-grid">
                <v-text-field
                  v-for="field in searchFields"
                  :key="field.key"
                  v-model="searchData[field.key]"
                  :prepend-icon="field.icon"
                  :label="field.label"
                  :prefix="field.prefix"
                  :placeholder="field.placeholder"
                  outline
                  clearable
                  color="#fddf00"
                  @input="handleSearchInput"
                  class="search-field"
                ></v-text-field>
              </div>

              <!-- Quick Actions -->
              <div class="quick-actions" v-if="hasActiveFilters">
                <v-btn
                  small
                  outlined
                  color="grey"
                  @click="clearAllFilters"
                  class="clear-filters-btn"
                >
                  <v-icon small left>clear</v-icon>
                  Clear All Filters
                </v-btn>
              </div>
            </div>
          </v-expand-transition>

          <!-- Collapsed Quick Search -->
          <div v-show="!isSearchExpanded" class="collapsed-search">
            <v-text-field
              v-model="quickSearchValue"
              prepend-icon="search"
              label="Quick search (full name, email, phone)..."
              outline
              clearable
              color="#fddf00"
              @input="handleQuickSearch"
              class="quick-search-field"
              hint="Search by full name, email, or phone number"
              persistent-hint
            ></v-text-field>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Modern Data Table -->
    <div class="table-section">
      <v-card class="table-card" elevation="2">
        <v-card-title class="table-header">
          <div class="table-title-section">
            <h3 class="table-title">Customer List</h3>
            <p class="table-subtitle">{{ totalItems }} customers found</p>
          </div>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="customerList"
          :loading="loadingMessage"
          hide-actions
          class="modern-table"
        >
          <template slot="items" slot-scope="props">
            <tr class="table-row" :class="{ 'vip-row': props.item.user_vip }">
              <!-- Row Number -->
              <td class="table-cell number-cell text-xs-center">
                {{ getRowNumber(props.index) }}
              </td>

              <!-- Customer Name with VIP indicator -->
              <td class="table-cell text-xs-center">
                <div class="customer-name">
                  <v-icon
                    v-if="props.item.user_vip"
                    color="#fddf00"
                    small
                    class="vip-icon"
                  >
                    star
                  </v-icon>
                  {{ props.item.user_first_name }}
                </div>
              </td>

              <!-- Basic Info -->
              <td class="table-cell text-xs-center">
                {{ props.item.user_last_name }}
              </td>

              <!-- Points with styling -->
              <td class="table-cell text-xs-center">
                <v-chip
                  :color="getPointsColor(props.item.user_sum_point)"
                  small
                  class="point-chip"
                >
                  {{ props.item.user_sum_point }} pts
                </v-chip>
              </td>

              <!-- Contact Info -->
              <td class="table-cell email-cell text-xs-center">
                {{ props.item.user_email }}
              </td>
              <td class="table-cell text-xs-center">
                {{ props.item.user_country_tel }}
              </td>
              <td class="table-cell text-xs-center">
                {{ props.item.user_tel }}
              </td>
              <td class="table-cell user-id-cell text-xs-center">
                {{ props.item.user_id }}
              </td>

              <!-- User Type -->
              <td class="table-cell text-xs-center">
                <v-chip
                  :color="getTypeColor(props.item.user_type)"
                  small
                  :class="{ 'facebook-chip': props.item.user_type == '2' }"
                >
                  {{ getUserType(props.item.user_type) }}
                </v-chip>
              </td>

              <!-- Join Date -->
              <td class="table-cell date-cell text-xs-center">
                {{ formatDate(props.item.user_create_at) }}
              </td>

              <!-- Address Button -->
              <td class="table-cell text-xs-center">
                <v-btn
                  :loading="fetching_address === props.item.user_id"
                  icon
                  @click="editAddress(props.item)"
                  color="#fddf00"
                  small
                >
                  <v-icon small>remove_red_eye</v-icon>
                </v-btn>
              </td>

              <!-- VIP Checkbox -->
              <td class="table-cell text-xs-center">
                <v-checkbox-otp
                  otpTel="974563956"
                  v-model="props.item.user_vip"
                  @change="setVIP($event, props.item)"
                  color="#fddf00"
                />
              </td>

              <!-- Actions -->
              <td
                class="table-cell actions-cell justify-center layout px-0"
                v-if="props.index !== indexLoading"
              >
                <div class="action-buttons-group">
                  <v-tooltip top>
                    <v-btn
                      icon
                      :disabled="isEditCustomer"
                      @click="editItem(props.item)"
                      color="primary"
                      small
                      slot="activator"
                    >
                      <v-icon small>edit</v-icon>
                    </v-btn>
                    <span>Edit</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <v-btn
                      icon
                      :disabled="isEditCustomer"
                      @click="deleteItem(props.item)"
                      color="error"
                      small
                      slot="activator"
                    >
                      <v-icon small>delete</v-icon>
                    </v-btn>
                    <span>Delete</span>
                  </v-tooltip>
                </div>
              </td>

              <!-- Loading State -->
              <td class="table-cell justify-center layout px-0" v-else>
                <div class="editing-indicator">
                  <v-progress-circular
                    indeterminate
                    color="#fddf00"
                    size="20"
                  ></v-progress-circular>
                  <span>Editing...</span>
                </div>
              </td>
            </tr>
          </template>

          <template slot="no-data">
            <div class="no-data-container">
              <div v-if="loadingMessage">
                <v-progress-circular
                  :size="30"
                  color="primary"
                  indeterminate
                ></v-progress-circular>
                Loading data...
              </div>
              <div v-else style="text-align: center; padding: 2rem">
                <v-icon color="#fddf00" size="80">search</v-icon>
                <h3 class="no-data-title">No customers found</h3>
                <p class="no-data-subtitle">
                  Try adjusting your search criteria
                </p>
              </div>
            </div>
          </template>
        </v-data-table>

        <!-- Modern Pagination -->
        <v-divider></v-divider>
        <div class="pagination-section">
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            @input="getData()"
            color="#fddf00"
            class="modern-pagination"
            :total-visible="7"
          ></v-pagination>
        </div>
      </v-card>
    </div>

    <!-- Dialogs -->
    <newCustomerForm
      :dialog="newCustomerDialog"
      @setDialog="newCustomerDialog = $event"
      v-if="!isEditCustomer"
    />
    <editCustomerForm
      :original="originalCustomer"
      :dialog="editCustomerDialog"
      @setDialog="editCustomerDialog = $event"
      :selectedCustomer="selectedCustomer"
      @cancel="cancelEdit()"
      @password-reset-success="handlePasswordResetSuccess"
      @password-reset-error="handlePasswordResetError"
      v-if="!isEditCustomer"
    />
    <addressList
      :selectedCustomer="selectedCustomer"
      :dialog="addressListDialog"
      @setDialog="addressListDialog = $event"
      @cancel="cancelEdit()"
    />

    <!-- Success Snackbar -->
    <v-snackbar v-model="successSnackbar" color="success" :timeout="4000">
      <v-icon color="white">check_circle</v-icon>
      {{ successMessage }}
      <v-btn color="white" text @click="successSnackbar = false">Close</v-btn>
    </v-snackbar>

    <!-- Error Snackbar -->
    <v-snackbar v-model="errSnackbar" color="error" :timeout="3000">
      <v-icon color="white">error</v-icon>
      {{ snackBarMsg || "Something went wrong!" }}
      <v-btn color="white" text @click="errSnackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import newCustomerForm from "./NewCustomerForm.vue";
import editCustomerForm from "./EditCustomerForm.vue";
import addressList from "./AddressList.vue";
import { mapState, mapGetters, mapActions } from "vuex";
import _ from "lodash";
import moment from "moment";

export default {
  components: {
    newCustomerForm,
    editCustomerForm,
    addressList,
  },

  data: () => ({
    errSnackbar: false,
    snackBarMsg: null,
    customerList: [],
    currentPage: 1,
    totalPages: 0,
    pageItems: 20,
    totalItems: 0,
    fetching_address: null,
    originalCustomer: null,
    selectedCustomer: null,
    loadingMessage: false,
    addressListDialog: false,
    newCustomerDialog: false,
    editCustomerDialog: false,

    // Consolidated search data
    searchData: {
      firstName: "",
      lastName: "",
      tel: "",
      email: "",
    },

    // Search field configuration
    searchFields: [
      {
        key: "firstName",
        icon: "person",
        label: "Search by first name",
        prefix: null,
        placeholder: null,
      },
      {
        key: "lastName",
        icon: "person",
        label: "Search by last name",
        prefix: null,
        placeholder: null,
      },
      {
        key: "tel",
        icon: "phone",
        label: "Search by phone number",
        prefix: "0",
        placeholder: "#########",
      },
      {
        key: "email",
        icon: "email",
        label: "Search by email",
        prefix: null,
        placeholder: null,
      },
    ],

    // Optimized headers configuration
    headers: [
      {
        text: "#",
        align: "center",
        sortable: false,
        value: "number",
        width: "80px",
      },
      {
        text: "First Name",
        align: "center",
        value: "user_first_name",
        sortable: false,
      },
      {
        text: "Last Name",
        align: "center",
        value: "user_last_name",
        sortable: false,
      },
      {
        text: "Points",
        align: "center",
        value: "user_sum_point",
        sortable: false,
        width: "120px",
      },
      { text: "Email", align: "center", value: "user_email", sortable: false },
      {
        text: "Country",
        align: "center",
        value: "user_country_tel",
        sortable: false,
        width: "100px",
      },
      { text: "Phone", align: "center", value: "user_tel", sortable: false },
      {
        text: "User ID",
        align: "center",
        value: "user_id",
        sortable: false,
        width: "100px",
      },
      {
        text: "Type",
        align: "center",
        value: "user_type_name_temp",
        sortable: false,
        width: "120px",
      },
      {
        text: "Join Date",
        align: "center",
        value: "user_create_at",
        sortable: false,
        width: "150px",
      },
      {
        text: "Address",
        align: "center",
        value: "address",
        sortable: false,
        width: "100px",
      },
      {
        text: "VIP",
        align: "center",
        value: "user_vip",
        sortable: false,
        width: "80px",
      },
      {
        text: "Actions",
        align: "center",
        value: "actions",
        sortable: false,
        width: "120px",
      },
    ],

    // User type mapping
    userTypeMap: {
      1: "Weserve",
      2: "Facebook",
      default: "Other",
    },

    // Color mappings
    typeColorMap: {
      1: "#fddf00",
      2: "#4267B2",
      default: "grey lighten-2",
    },

    // New expansion state
    isSearchExpanded: true,
    quickSearchValue: "",

    // Success snackbar state
    successSnackbar: false,
    successMessage: "",
  }),

  computed: {
    ...mapState({
      customerData: (state) => state.fetchCustomerBack.customerData,
      indexLoading: (state) => state.fetchCustomerBack.indexLoading,
    }),
    ...mapGetters(["isEditCustomer"]),

    hasActiveFilters() {
      return Object.values(this.searchData).some(
        (value) => value && value.trim() !== ""
      );
    },
  },

  created() {
    // Restore expansion state from localStorage
    const savedExpansion = localStorage.getItem("customerSearchExpanded");
    if (savedExpansion !== null) {
      this.isSearchExpanded = savedExpansion === "true";
    }

    this.getData();
  },

  destroyed() {
    this.$store.commit("fetchCustomerBack/resetState");
  },

  methods: {
    ...mapActions({
      setUserToVIP: "fetchUserBack/setUserToVIP",
    }),

    // Optimized search input handler
    handleSearchInput: _.debounce(function () {
      this.loadingMessage = true;
      this.currentPage = 1;
      this.getData();
    }, 1000),

    // Utility methods
    getRowNumber(index) {
      return this.currentPage * this.pageItems - (this.pageItems - index - 1);
    },

    getPointsColor(points) {
      return points > 1000 ? "#fddf00" : "grey lighten-2";
    },

    getTypeColor(userType) {
      return this.typeColorMap[userType] || this.typeColorMap.default;
    },

    getUserType(userType) {
      return this.userTypeMap[userType] || this.userTypeMap.default;
    },

    formatDate(dateString) {
      return moment(dateString).format("YYYY-MM-DD HH:mm");
    },

    // Main methods
    toAdd() {
      this.newCustomerDialog = true;
      this.$store.dispatch("fetchCustomerBack/getProvinceAdd");
    },

    async setVIP(value, item) {
      try {
        const result = await this.setUserToVIP({
          user_id: item.user_id,
          user_level_status: value.status,
          otp_pin: value.otp_pin,
          otp_ref: value.otp_ref,
          otp_tel: value.otp_tel,
        });

        if (result === false || result.msg === false) {
          item.user_vip = !item.user_vip;
          this.showError(result.data);
        }
      } catch (error) {
        item.user_vip = !item.user_vip;
        this.showError("Failed to update VIP status");
      }
    },

    async editAddress(item) {
      this.selectedCustomer = item;
      this.fetching_address = item.user_id;

      try {
        this.selectedCustomer.user_address = await this.$store.dispatch(
          "fetchCustomerBack/getCustomerAddressDetail",
          item.user_id
        );
        this.addressListDialog = true;
        this.$store.dispatch("fetchCustomerBack/getProvinceAdd");
      } catch (error) {
        this.showError("Failed to load address details");
      } finally {
        this.fetching_address = null;
      }
    },

    cancelEdit() {
      this.originalCustomer = null;
      this.selectedCustomer = null;
    },

    editItem(item) {
      this.originalCustomer = Object.assign({}, item);
      this.selectedCustomer = item;
      this.editCustomerDialog = true;
    },

    async deleteItem(item) {
      if (!confirm("Are you sure you want to delete this user?")) {
        return;
      }

      try {
        const result = await this.$store.dispatch(
          "fetchCustomerBack/deleteCustomer",
          item
        );
        if (result) {
          this.getData();
        }
      } catch (error) {
        this.showError("Failed to delete customer");
      }
    },

    async getData() {
      this.loadingMessage = true;

      try {
        const config = {
          search_first_name: this.searchData.firstName,
          search_last_name: this.searchData.lastName,
          search_tel: this.searchData.tel,
          search_email: this.searchData.email,
          page_size: this.pageItems,
          current_page: this.currentPage,
        };

        const response = await this.$store.dispatch(
          "fetchCustomerBack/getData",
          config
        );
        this.customerList = response.data;
        this.totalPages = Math.ceil(response.total / this.pageItems);
        this.totalItems = response.total;
      } catch (error) {
        this.showError("Failed to load customer data");
      } finally {
        this.loadingMessage = false;
      }
    },

    // New expansion methods
    toggleSearchExpansion() {
      this.isSearchExpanded = !this.isSearchExpanded;

      // Save expansion state to localStorage
      localStorage.setItem(
        "customerSearchExpanded",
        this.isSearchExpanded.toString()
      );

      // If collapsing and there are filters, consolidate them into quick search
      if (!this.isSearchExpanded && this.hasActiveFilters) {
        this.consolidateFiltersToQuickSearch();
      }

      // If expanding and there's a quick search value, clear it
      if (this.isSearchExpanded && this.quickSearchValue) {
        this.quickSearchValue = "";
      }
    },

    getActiveFiltersText() {
      if (!this.hasActiveFilters && !this.quickSearchValue) {
        return "Click to expand search options";
      }

      const activeFilters = [];
      Object.entries(this.searchData).forEach(([key, value]) => {
        if (value && value.trim() !== "") {
          const field = this.searchFields.find((f) => f.key === key);
          activeFilters.push(
            `${field.label.replace("Search by ", "")}: ${value}`
          );
        }
      });

      if (this.quickSearchValue) {
        activeFilters.push(`Quick search: "${this.quickSearchValue}"`);
      }

      return activeFilters.length > 0
        ? `Active filters: ${activeFilters.join(", ")}`
        : "Click to expand search options";
    },

    handleQuickSearch: _.debounce(function () {
      // When using quick search, clear individual filters and search across multiple fields
      if (this.quickSearchValue && this.quickSearchValue.trim() !== "") {
        // Clear individual filters first
        Object.keys(this.searchData).forEach((key) => {
          this.searchData[key] = "";
        });

        const searchTerm = this.quickSearchValue.trim();

        // Determine search strategy based on input
        if (this.isEmailPattern(searchTerm)) {
          // If it looks like an email, search email field
          this.searchData.email = searchTerm;
        } else if (this.isPhonePattern(searchTerm)) {
          // If it looks like a phone number, search phone field
          this.searchData.tel = searchTerm.replace(/^0+/, ""); // Remove leading zeros
        } else {
          // For names and general text, use a combination approach
          // Split the search term to handle full names
          const searchWords = searchTerm
            .split(/\s+/)
            .filter((word) => word.length > 0);

          if (searchWords.length === 1) {
            // Single word - could be first name, last name, or partial
            this.searchData.firstName = searchTerm;
          } else if (searchWords.length >= 2) {
            // Multiple words - assume first word is first name, rest is last name
            this.searchData.firstName = searchWords[0];
            this.searchData.lastName = searchWords.slice(1).join(" ");
          }
        }
      } else {
        // Clear all search data if quick search is empty
        Object.keys(this.searchData).forEach((key) => {
          this.searchData[key] = "";
        });
      }

      this.loadingMessage = true;
      this.currentPage = 1;
      this.getData();
    }, 1000),

    // Helper methods for pattern detection
    isEmailPattern(text) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(text) || text.includes("@");
    },

    isPhonePattern(text) {
      // Remove all non-digit characters for pattern matching
      const digitsOnly = text.replace(/\D/g, "");
      // Thai phone numbers are typically 9-10 digits (without country code)
      // or start with common patterns
      return (
        /^[0-9+\-\s()]{8,}$/.test(text) ||
        (digitsOnly.length >= 8 && digitsOnly.length <= 15)
      );
    },

    // Enhanced search data consolidation
    consolidateFiltersToQuickSearch() {
      // Combine all active filters into a single quick search value
      const activeEntries = Object.entries(this.searchData).filter(
        ([key, value]) => value && value.trim() !== ""
      );

      if (activeEntries.length > 0) {
        // Smart consolidation based on field types
        const consolidatedParts = [];

        activeEntries.forEach(([key, value]) => {
          if (key === "firstName" || key === "lastName") {
            consolidatedParts.push(value);
          } else if (key === "email") {
            // Email gets priority in consolidation
            this.quickSearchValue = value;
            return;
          } else if (key === "tel") {
            // Phone gets priority in consolidation
            this.quickSearchValue = `0${value}`;
            return;
          }
        });

        // If no email or phone, combine name parts
        if (!this.quickSearchValue && consolidatedParts.length > 0) {
          this.quickSearchValue = consolidatedParts.join(" ");
        }
      }
    },

    clearAllFilters() {
      // Reset all search data
      Object.keys(this.searchData).forEach((key) => {
        this.searchData[key] = "";
      });
      this.quickSearchValue = "";
      this.handleSearchInput();
    },

    // Error handling
    showError(message) {
      this.errSnackbar = true;
      this.snackBarMsg = message;
    },

    // Handle password reset success
    handlePasswordResetSuccess(event) {
      this.successMessage = `Password reset successfully for ${event.customer.user_first_name} ${event.customer.user_last_name}`;
      this.successSnackbar = true;
    },

    // Handle password reset error
    handlePasswordResetError(event) {
      this.showError(
        `Failed to reset password for ${event.customer.user_first_name} ${event.customer.user_last_name}`
      );
    },

    // Remove unused methods
    moment,
  },
};
</script>

<style scoped>
.customer-list-container {
  background: transparent;
}

.search-filter-section {
  margin-bottom: 2rem;
}

.search-filter-card {
  border-radius: 16px;
  border: 1px solid rgba(253, 223, 0, 0.2);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
}

.search-filter-content {
  padding: 2rem;
}

.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  gap: 2rem;
}

.search-title {
  flex: 1;
}

.filter-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-subtitle {
  color: #7f8c8d;
  margin: 0;
}

.add-customer-btn {
  color: black !important;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  flex-shrink: 0;
}

/* Remove refresh button styles */
.refresh-btn {
  display: none;
}

.search-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.search-field {
  background: white;
  border-radius: 8px;
}

.table-section {
  margin-bottom: 2rem;
}

.table-card {
  border-radius: 16px;
  border: 1px solid rgba(253, 223, 0, 0.2);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  overflow: hidden;
}

.table-header {
  background: linear-gradient(
    135deg,
    rgba(253, 223, 0, 0.1) 0%,
    rgba(253, 223, 0, 0.05) 100%
  );
  border-bottom: 1px solid rgba(253, 223, 0, 0.2);
  padding: 1.5rem 2rem;
}

.table-title-section {
  width: 100%;
}

.table-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.25rem;
}

.table-subtitle {
  color: #7f8c8d;
  margin: 0;
  font-size: 0.9rem;
}

.modern-table {
  background: transparent;
}

.table-row {
  transition: all 0.2s ease;
}

.table-row:hover {
  background-color: rgba(253, 223, 0, 0.05) !important;
}

.vip-row {
  background-color: rgba(253, 223, 0, 0.08) !important;
}

.table-cell {
  padding: 1rem 0.75rem !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
  font-size: 0.9rem;
}

.number-cell {
  font-weight: 600;
  color: #6b7280;
}

.customer-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}

.vip-icon {
  opacity: 0.8;
}

.point-chip {
  font-weight: 600;
  font-size: 0.75rem;
}

.email-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-id-cell {
  font-family: "Courier New", monospace;
  color: #6b7280;
  font-size: 0.8rem;
}

.date-cell {
  color: #6b7280;
  font-size: 0.8rem;
}

.actions-cell {
  padding: 0.5rem !important;
}

.action-buttons-group {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.editing-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.8rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
}

.loading-text {
  color: #6b7280;
  font-size: 1.1rem;
}

.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
}

.no-data-title {
  color: #2c3e50;
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0;
}

.no-data-subtitle {
  color: #7f8c8d;
  margin: 0;
}

.pagination-section {
  padding: 1.5rem 2rem;
  background: rgba(253, 223, 0, 0.02);
  border-top: 1px solid rgba(253, 223, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modern-pagination {
  color: #2c3e50;
}

.expansion-btn {
  transition: transform 0.3s ease;
  background-color: rgba(253, 223, 0, 0.1) !important;
  border: 1px solid rgba(253, 223, 0, 0.3);
}

.expansion-btn--expanded {
  transform: rotate(0deg);
}

.expansion-btn:not(.expansion-btn--expanded) {
  transform: rotate(0deg);
}

.expansion-btn:hover {
  background-color: rgba(253, 223, 0, 0.2) !important;
}

.filter-subtitle-collapsed {
  color: #7f8c8d;
  margin: 0;
  font-size: 0.9rem;
  font-style: italic;
}

.search-expansion-content {
  margin-top: 1rem;
}

.collapsed-search {
  margin-top: 1rem;
}

.quick-search-field {
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
}

.quick-search-field .v-input__details {
  padding-top: 8px;
}

.quick-search-field .v-messages {
  color: #6b7280 !important;
  font-size: 0.75rem;
}

.quick-actions {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(253, 223, 0, 0.2);
  display: flex;
  justify-content: flex-end;
}

.clear-filters-btn {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}

/* Animation for smooth transitions */
.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease;
}

.v-expand-transition-enter,
.v-expand-transition-leave-to {
  opacity: 0;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .filter-title {
    font-size: 1.2rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .expansion-btn {
    align-self: flex-end;
    margin-top: -1.5rem;
  }

  .quick-actions {
    justify-content: center;
  }

  .clear-filters-btn {
    width: 100%;
    max-width: 200px;
  }
}

@media (max-width: 480px) {
  .action-bar {
    flex-direction: column;
    gap: 1rem;
  }

  .search-title {
    width: 100%;
  }

  .add-customer-btn {
    width: 100%;
  }
}

/* Facebook chip specific styling */
.facebook-chip {
  color: white !important;
}

.facebook-chip .v-chip__content {
  color: white !important;
}
</style>
